import { formatMoney } from '@expenditure/core';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useToast } from '../components/Toast';
import { Avatar, CategoryIcon, ErrorState, PageSkeleton, SectionTitle } from '../components/ui';
import { api } from '../lib/api';
import { errorMessage, formatDate } from '../lib/format';
import { useExpense, useGroup, useInvalidateGroup } from '../lib/queries';
import { confirmDialog } from '../lib/telegram';

export function ExpenseDetailPage() {
  const { groupId = '', expenseId = '' } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidateGroup();
  const group = useGroup(groupId);
  const expense = useExpense(groupId, expenseId);

  if (group.isPending || expense.isPending) return <PageSkeleton />;
  const error = group.error ?? expense.error;
  if (error || !group.data || !expense.data) return <ErrorState message={errorMessage(error, t)} />;

  const e = expense.data;
  const { me, members, formerMembers } = group.data;
  const memberById = new Map([...formerMembers, ...members].map((m) => [m.id, m]));
  const nameOf = (id: string) => (id === me.id ? t('common.you') : (memberById.get(id)?.displayName ?? '—'));

  const remove = async () => {
    if (!(await confirmDialog(t('expense.deleteConfirm')))) return;
    try {
      await api.deleteExpense(groupId, expenseId);
      await invalidate(groupId);
      toast(t('expense.deleted'));
      void navigate(`/groups/${groupId}`, { replace: true });
    } catch (err) {
      toast(errorMessage(err, t), 'error');
    }
  };

  return (
    <main className="page page--no-tabbar app">
      <div className="card stack" style={{ alignItems: 'center', textAlign: 'center', gap: 8 }}>
        <CategoryIcon category={e.category} />
        <div className="list-title" style={{ fontSize: 18 }}>
          {e.description}
        </div>
        <div className="num" style={{ fontSize: 36, fontWeight: 800 }}>
          {formatMoney(e.amountMinor, e.currency)}
        </div>
        {e.originalCurrency !== e.currency && (
          <div className="muted small num">
            {e.exchangeRate
              ? t('expense.original', { amount: formatMoney(e.originalAmountMinor, e.originalCurrency), rate: e.exchangeRate })
              : t('expense.originalPlain', { amount: formatMoney(e.originalAmountMinor, e.originalCurrency) })}
          </div>
        )}
        <div className="row" style={{ flexWrap: 'wrap', justifyContent: 'center', gap: 6 }}>
          <span className="badge badge--primary">{t(`categories.${e.category}`)}</span>
          <span className="badge">{formatDate(e.expenseDate, i18n.language)}</span>
          <span className="badge">{t(`splitMethods.${e.splitMethod}`)}</span>
        </div>
        <div className="muted">{t('group.paidBy', { name: nameOf(e.paidByMemberId) })}</div>
        {e.note && <p className="muted small" style={{ margin: 0 }}>{e.note}</p>}
      </div>

      <SectionTitle>{t('expense.splitBetween')}</SectionTitle>
      <div className="card card--flush list">
        {e.splits.map((s) => {
          const member = memberById.get(s.memberId);
          return (
            <div className="list-row" key={s.memberId} style={{ cursor: 'default' }}>
              <Avatar name={member?.displayName ?? '?'} photo={member?.profilePhoto} small />
              <div className="grow list-title ellipsis">{nameOf(s.memberId)}</div>
              <div className="list-end">
                <div className="list-title num">{formatMoney(s.amountMinor, e.currency)}</div>
                {s.inputValue && e.splitMethod !== 'exact' && (
                  <div className="list-sub num">
                    {e.splitMethod === 'percentage' ? `${s.inputValue}%` : `× ${s.inputValue}`}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {e.canEdit && (
        <div className="row">
          <Link className="btn btn--soft grow" to={`/groups/${groupId}/expenses/${expenseId}/edit`}>
            {t('common.edit')}
          </Link>
          <button className="btn btn--danger grow" onClick={() => void remove()}>
            {t('common.delete')}
          </button>
        </div>
      )}
    </main>
  );
}
